import React, { lazy } from 'react';
import { Redirect } from 'react-router-dom';

import AuthGuard from './components/AuthGuard';

import AuthLayout from './layouts/Auth';
import ErrorLayout from './layouts/Error';
import DashboardLayout from './layouts/Dashboard';
import DashboardAnalyticsView from './views/DashboardAnalytics';
import DashboardDefaultView from './views/DashboardDefault';
import OverviewView from './views/Overview';
import Home from './views/Home';
import PresentationView from './views/Presentation';
import Users from './views/Management/components/Users';
import Operators from './views/Management/components/Operators';
import OperatorDetails from './views/Management/components/OperatorDetails';
import Settings from './views/Management/components/Settings';
import DashboardInspect from './views/Dashboard/components/Inspect/DashboardInspect.js';
import StepIn from './views/StepInFollowUp/StepIn.js';
import DataManagement from './views/DataManagement/DataManagement.js';
import Outliers from './views/Shutdown3g/components/Shutdown3g.js';
import ActivitiesTracker from './views/ActivityTracker/components/TrackerPage.jsx';
import ActivitiesMap from './views/ActivityMap/App.jsx';
import ActivityKPIDashboard from './views/ActivityPerformance/main.jsx';
import AnalyticsDashboard from './views/AnalyticsDashboard/index.jsx';
import TrackableRoute from './views/AnalyticsDashboard/TrackableRoute.js';
import Beacon4Blended from './views/Beacon4BlendedPerformance/main.jsx';
import Beacon4PerformanceNetworkOverview from './views/Beacon4PerformanceNetworkOverview/main.jsx';
import Beacon4Synthesis from './views/Beacon4Synthesis/main.jsx';
import RadioDesignUserPrivileges from './views/RadioDesignUserPrivileges/index.jsx';
import LockdownKPIDashboard from './views/LockdownKPIDashboard/main.jsx';
import LockdownManagement from './views/LockdownManagement/LockdownAggregation';
import EmailsWrongRight from './views/EmailsWrongRight/components/EmailsWrongRight';
import WeeklyUpdate from './views/WeeklyUpdate/index';
import TefInternal from './views/TefInternalMod/components/TefInternal';
import OldOutages from './views/OldOutagesMod/components/OldOutages';
import BackOnAir from './views/BackOnAirMod/components/BackOnAir';
import IncidentDashboard from './views/TelecomKPIAnomalyDetection/components/KpiIncidents/IncidentDashboard';
import KpiRulesPage from './views/TelecomKPIAnomalyDetection/components/KpiRules/KpiRulesPage';
import HelixCreations from './views/HelixCrqsMod/components/HelixCreations';
import HelixChanges from './views/HelixCrqsMod/components/HelixChanges';
import HelixOther from './views/HelixCrqsMod/components/HelixOther';
import HelixUpload from './views/HelixCrqsMod/components/HelixUpload';
import HelixStatistics from './views/HelixCrqsMod/components/HelixStatistics';
import NaspTracker from './views/NaspTracker/components/NaspTracker';
import DataIntegrity from './views/DataIntegrity/App';

const routes = [
	{
		path: '/',
		exact: true,
		component: () => <Redirect to="/home" />,
	},
	{
		path: '/home',
		exact: true,
		component: () => AuthGuard({
			children: DashboardLayout({
				route: {
					routes: [
						{
							path: '*',
							exact: true,
							component: (props) => AuthGuard({ children: Home(props) }),
						},
						{ component: () => <Redirect to="/errors/error-404" /> },
					],
				},
			}),
		}),
	},
	{
		path: '/DataManagement',
		exact: true,
		component: () => AuthGuard({
			roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS'],
			projects: ['VMO2 Ran', 'VMO2 Radio Design'],
			children: DashboardLayout({
				route: {
					routes: [
						{
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="DataManagement" moduleName="Data Management" projects="Management">
										{DataManagement()}
									</TrackableRoute>
								),
							}),
						},
						{ component: () => <Redirect to="/errors/error-404" /> },
					],
				},
			}),
		}),
	},
	{
		path: '/NetworkFaultsDetector',
		component: () => AuthGuard({
			children: DashboardLayout({
				route: {
					routes: [
						{
							path: '/NetworkFaultsDetector/5G',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
								projects: ['VMO2 Dashboards', 'VMO2 Ran'],
								children: (
									<TrackableRoute moduleId="NetworkFaultsDetector5G" moduleName="NetworkFaults Detector 5G" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 21 } }, pageTitle: 'NetworkFaultsDetector5G', module: 'Network Faults Detector 5G' })}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/NetworkFaultsDetector/4G',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS'],
								projects: ['VMO2 Dashboards', 'VMO2 Ran'],
								children: (
									<TrackableRoute moduleId="NetworkFaultsDetector4G" moduleName="NetworkFaults Detector 4G" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 25 } }, pageTitle: 'NetworkFaultsDetector4G', module: 'Network Faults Detector 4G' })}
									</TrackableRoute>
								),
							}),
						},
						{ component: () => <Redirect to="/errors/error-404" /> },
					],
				},
			}),
		}),
	},
	{
		path: '/Cluster',
		component: () => AuthGuard({
			children: DashboardLayout({
				route: {
					routes: [
						{
							path: '/Cluster/Management',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
								projects: ['VMO2 Dashboards', 'VMO2 Ran'],
								children: (
									<TrackableRoute moduleId="LockdownManagement" moduleName="Lockdown Management" projects="Beacon">
										{LockdownManagement()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/Cluster/LockdownKPI',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager'],
								projects: ['VMO2 Dashboards', 'VMO2 Ran'],
								children: (
									<TrackableRoute moduleId="LockdownKPI" moduleName="Lockdown KPI" projects="Beacon">
										{LockdownKPIDashboard()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/Cluster/DemandKPIsView',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
								projects: ['VMO2 Dashboards', 'VMO2 Ran'],
								children: (
									<TrackableRoute moduleId="Demand KPIs View" moduleName="Demand KPIs View" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 27 } }, pageTitle: 'DemandKPIsView', module: 'Demand KPIs View' })}
									</TrackableRoute>
								),
							}),
						},
						{ component: () => <Redirect to="/errors/error-404" /> },
					],
				},
			}),
		}),
	},
	{
		path: '/StepIn',
		component: () => AuthGuard({
			children: DashboardLayout({
				route: {
					routes: [
						{
							path: '/StepIn/Management',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS'],
								projects: ['VMO2 Dashboards', 'VMO2 Ran'],
								children: (
									<TrackableRoute moduleId="StepIn" moduleName="Step In" projects="Beacon">
										{StepIn()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/StepIn/Timeline',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
								projects: ['VMO2 Dashboards', 'VMO2 Ran'],
								children: (
									<TrackableRoute moduleId="StepInTimeline" moduleName="Step In Timeline" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 26 } }, pageTitle: 'Step In', module: 'Timeline' })}
									</TrackableRoute>
								),
							}),
						},
						{ component: () => <Redirect to="/errors/error-404" /> },
					],
				},
			}),
		}),
	},
	{
		path: '/Performance',
		exact: true,
		component: () => AuthGuard({
			roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
			projects: ['VMO2 Dashboards', 'VMO2 Ran'],
			children: DashboardLayout({
				route: {
					routes: [
						{
							path: '*',
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="Performance" moduleName="Performance" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 19 } }, pageTitle: 'Performance', module: 'Performance' })}
									</TrackableRoute>
								),
							}),
						},
						{ component: () => <Redirect to="/errors/error-404" /> },
					],
				},
			}),
		}),
	},
	{
		path: '/Network/Overview',
		exact: true,
		component: () => AuthGuard({
			roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
			projects: ['VMO2 Dashboards', 'VMO2 Ran', 'VMO2 Radio Design'],
			children: DashboardLayout({
				route: {
					routes: [
						{
							path: '*',
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="NetworkOverview" moduleName="Network Overview" projects="Beacon">
										{Beacon4PerformanceNetworkOverview()}
									</TrackableRoute>
								),
							}),
						},
						{ component: () => <Redirect to="/errors/error-404" /> },
					],
				},
			}),
		}),
	},
	{
		path: '/Blended',
		component: () => AuthGuard({
			children: DashboardLayout({
				route: {
					routes: [
						{
							path: '/Blended/MonthlyBlended1',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
								projects: ['VMO2 Dashboards', 'VMO2 Ran'],
								children: (
									<TrackableRoute moduleId="Monthly BlendedPack1" moduleName="Monthly Blended (Pack 1)" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 22 } }, pageTitle: 'Monthly Blended', module: 'Blended (Pack 1)' })}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/Blended/MonthlyBlended2',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
								projects: ['VMO2 Dashboards', 'VMO2 Ran'],
								children: (
									<TrackableRoute moduleId="Monthly BlendedPack2" moduleName="Monthly Blended (Pack 2)" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 24 } }, pageTitle: 'Monthly Blended', module: 'Blended (Pack 2)' })}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/Blended/WeeklyBlended',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
								projects: ['VMO2 Dashboards', 'VMO2 Ran'],
								children: (
									<TrackableRoute moduleId="Weekly Blended" moduleName="Weekly Blended" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 23 } }, pageTitle: 'Weekly Blended', module: 'Blended' })}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/Blended/Performance',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
								projects: ['VMO2 Dashboards', 'VMO2 Ran'],
								children: (
									<TrackableRoute moduleId="PerformanceBlended" moduleName="Performance Blended" projects="Beacon">
										{Beacon4Blended()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/Blended/Synthesis',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS'],
								projects: ['VMO2 Ran'],
								children: (
									<TrackableRoute moduleId="Synthesis" moduleName="Synthesis" projects="Beacon">
										{Beacon4Synthesis()}
									</TrackableRoute>
								),
							}),
						},
						{ component: () => <Redirect to="/errors/error-404" /> },
					],
				},
			}),
		}),
	},
	{
		path: '/Shutdown3g',
		component: () => AuthGuard({
			children: DashboardLayout({
				route: {
					routes: [
						{
							path: '/Shutdown3g/ClusterKpi/Hourly',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
								projects: ['3GSD'],
								children: (
									<TrackableRoute moduleId="3GSDClusterKPIHourly" moduleName="3GSD Cluster KPI Hourly" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 28 } }, pageTitle: '3GSD', module: 'Cluster KPI Hourly' })}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/Shutdown3g/ClusterKpi/Daily',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
								projects: ['3GSD'],
								children: (
									<TrackableRoute moduleId="3GSDClusterKPIDaily" moduleName="3GSD Cluster KPI Daily" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 31 } }, pageTitle: '3GSD', module: 'Cluster KPI Daily' })}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/Shutdown3g/CellKpi/Hourly/2G',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS'],
								projects: ['3GSD'],
								children: (
									<TrackableRoute moduleId="3GSDCellKPIHourly2G" moduleName="3GSD Cell KPI Hourly 2G" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 34 } }, pageTitle: '3GSD', module: 'Cell KPI Hourly - 2G' })}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/Shutdown3g/CellKpi/Hourly/3G',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS'],
								projects: ['3GSD'],
								children: (
									<TrackableRoute moduleId="3GSDCellKPIHourly3G" moduleName="3GSD Cell KPI Hourly 3G" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 38 } }, pageTitle: '3GSD', module: 'Cell KPI Hourly - 3G' })}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/Shutdown3g/CellKpi/Hourly/4G',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS'],
								projects: ['3GSD'],
								children: (
									<TrackableRoute moduleId="3GSDCellKPIHourly4G" moduleName="3GSD Cell KPI Hourly 4G" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 39 } }, pageTitle: '3GSD', module: 'Cell KPI Hourly - 4G' })}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/Shutdown3g/CellKpi/Daily/2G',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS'],
								projects: ['3GSD'],
								children: (
									<TrackableRoute moduleId="3GSDCellKPIDaily2G" moduleName="3GSD Cell KPI Daily 2G" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 35 } }, pageTitle: '3GSD', module: 'Cell KPI Daily - 2G' })}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/Shutdown3g/CellKpi/Daily/3G',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS'],
								projects: ['3GSD'],
								children: (
									<TrackableRoute moduleId="3GSDCellKPIDaily3G" moduleName="3GSD Cell KPI Daily 3G" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 40 } }, pageTitle: '3GSD', module: 'Cell KPI Daily - 3G' })}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/Shutdown3g/CellKpi/Daily/4G',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS'],
								projects: ['3GSD'],
								children: (
									<TrackableRoute moduleId="3GSDCellKPIDaily4G" moduleName="3GSD Cell KPI Daily 4G" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 41 } }, pageTitle: '3GSD', module: 'Cell KPI Daily - 4G' })}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/Shutdown3g/Outliers',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
								projects: ['3GSD'],
								children: (
									<TrackableRoute moduleId="3GSDOutliers" moduleName="3GSD Outliers" projects="Beacon">
										{Outliers()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/Shutdown3g/DataIntegrity',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ'],
								projects: ['3GSD'],
								children: (
									<TrackableRoute moduleId="3GSDDataIntegrity" moduleName="3GSD Data Integrity" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 42 } }, pageTitle: '3GSD', module: 'Data Integrity' })}
									</TrackableRoute>
								),
							}),
						},
						{ component: () => <Redirect to="/errors/error-404" /> },
					],
				},
			}),
		}),
	},
	{
		path: '/TKAD',
		component: () => AuthGuard({
			children: DashboardLayout({
				route: {
					routes: [
						{
							path: '/TKAD/Incidents',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
								projects: ['VMO2 Dashboards', 'VMO2 Ran', '3GSD', 'VMO2 Radio Design'],
								children: (
									<TrackableRoute moduleId="TKADIncidents" moduleName="TKAD Incidents" projects="Beacon">
										<IncidentDashboard />
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/TKAD/Rules',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager'],
								projects: ['VMO2 Dashboards', 'VMO2 Ran', '3GSD', 'VMO2 Radio Design'],
								children: (
									<TrackableRoute moduleId="TKADRules" moduleName="TKAD Rules" projects="Beacon">
										<KpiRulesPage />
									</TrackableRoute>
								),
							}),
						},
						{ component: () => <Redirect to="/errors/error-404" /> },
					],
				},
			}),
		}),
	},
	{
		path: '/RadioDesign',
		component: () => AuthGuard({
			children: DashboardLayout({
				route: {
					routes: [
						{
							path: '/RadioDesign/TrafficTrends',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
								projects: ['VMO2 Radio Design Traffic Trends'],
								children: (
									<TrackableRoute moduleId="TrafficTrends" moduleName="Traffic Trends" projects="Radio Design">
										{DashboardInspect({ match: { params: { id: 43 } }, pageTitle: 'Radio Design', module: 'Traffic Trends' })}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/RadioDesign/WeeklyUpdate',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
								projects: ['VMO2 Radio Design'],
								children: (
									<TrackableRoute moduleId="WeeklyUpdate" moduleName="Weekly Update" projects="Radio Design">
										{WeeklyUpdate()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/RadioDesign/ActivitiesTracker',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
								projects: ['VMO2 Ran', 'VMO2 Radio Design'],
								children: (
									<TrackableRoute moduleId="ActivitiesTracker" moduleName="Activities Tracker" projects="Radio Design">
										{ActivitiesTracker()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/RadioDesign/ActivitiesMap',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
								projects: ['VMO2 Ran', 'VMO2 Radio Design'],
								children: (
									<TrackableRoute moduleId="ActivitiesMap" moduleName="Activities Map" projects="Radio Design">
										{ActivitiesMap()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/RadioDesign/ActivityPerformance',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
								projects: ['VMO2 Ran', 'VMO2 Radio Design'],
								children: (
									<TrackableRoute moduleId="ActivityPerformance" moduleName="Activity Performance" projects="Radio Design">
										{ActivityKPIDashboard()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/RadioDesign/UserPrivileges',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager'],
								projects: ['VMO2 Ran', 'VMO2 Radio Design'],
								children: (
									<TrackableRoute moduleId="User Privileges" moduleName="User Privileges" projects="Radio Design">
										{RadioDesignUserPrivileges()}
									</TrackableRoute>
								),
							}),
						},
						{ component: () => <Redirect to="/errors/error-404" /> },
					],
				},
			}),
		}),
	},
	{
		path: '/EstimateCustomerExperience',
		exact: true,
		component: () => AuthGuard({
			roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
			projects: ['VMO2 Dashboards', 'VMO2 Ran'],
			children: DashboardLayout({
				route: {
					routes: [
						{
							path: '*',
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="EstimateCustomerExperience" moduleName="Estimate Customer Experience" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 18 } }, pageTitle: 'EstimateCustomerExperience', module: 'Estimate Customer Experience' })}
									</TrackableRoute>
								),
							}),
						},
						{ component: () => <Redirect to="/errors/error-404" /> },
					],
				},
			}),
		}),
	},
	{
		path: '/DemandKPIsView',
		exact: true,
		component: () => AuthGuard({
			roles: ['Administrator', 'Manager', 'CelfinetHQ', 'CelfinetOS', 'Client'],
			projects: ['VMO2 Dashboards', 'VMO2 Ran'],
			children: DashboardLayout({
				route: {
					routes: [
						{
							path: '*',
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="DemandKPIsView" moduleName="Demand KPIs View" projects="Beacon">
										{DashboardInspect({ match: { params: { id: 16 } }, pageTitle: 'DemandKPIsView', module: 'Demand KPIs View' })}
									</TrackableRoute>
								),
							}),
						},
						{ component: () => <Redirect to="/errors/error-404" /> },
					],
				},
			}),
		}),
	},
	{
		path: '/nasp',
		component: () => AuthGuard({
			children: DashboardLayout({
				route: {
					routes: [
						{
							path: '/nasp/dashboards/naspAvailability',
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="NaspAvailability" moduleName="NASP Availability" projects="NASP">
										{DashboardInspect({ match: { params: { id: 44 } }, pageTitle: 'NASP Availiability', module: 'Dashboards' })}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/nasp/dashboards/naspLongRunning',
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="NaspLongRunning" moduleName="NASP Long Running" projects="NASP">
										{DashboardInspect({ match: { params: { id: 45 } }, pageTitle: 'NASP Long Running', module: 'Dashboards' })}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/nasp/dashboards/naspQualityReport',
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="NaspQualityReport" moduleName="NASP Quality Report" projects="NASP">
										{DashboardInspect({ match: { params: { id: 46 } }, pageTitle: 'NASP Quality Report', module: 'Dashboards' })}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/nasp/emailsWrongRight',
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="EmailsWrongRight" moduleName="Emails Wrong Right" projects="NASP">
										{EmailsWrongRight()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/nasp/tefInternal',
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="TefInternal" moduleName="TEF Internal" projects="NASP">
										{TefInternal()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/nasp/oldOutages',
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="OldOutages" moduleName="Old Outages" projects="NASP">
										{OldOutages()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/nasp/backOnAir',
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="BackOnAir" moduleName="Back On Air" projects="NASP">
										{BackOnAir()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/nasp/helix/helixCreations',
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="Helix" moduleName="Helix Creations" projects="NASP">
										{HelixCreations()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/nasp/helix/helixChanges',
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="Helix" moduleName="Helix Changes" projects="NASP">
										{HelixChanges()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/nasp/helix/helixOther',
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="Helix" moduleName="Helix Other" projects="NASP">
										{HelixOther()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/nasp/helix/helixUpload',
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="Helix" moduleName="Helix Upload" projects="NASP">
										{HelixUpload()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/nasp/helix/helixStatistics',
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="Helix" moduleName="Helix Statistics" projects="NASP">
										{HelixStatistics()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/nasp/naspTracker',
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="Helix" moduleName="Helix Statistics" projects="NASP">
										{NaspTracker()}
									</TrackableRoute>
								),
							}),
						},
						{ component: () => <Redirect to="/errors/error-404" /> },
					],
				},
			}),
		}),
	},
	{
		path: '/management',
		component: () => AuthGuard({
			children: DashboardLayout({
				route: {
					routes: [
						{
							path: '/management/statistics',
							exact: true,
							component: () => AuthGuard({
								roles: ['Administrator', 'Manager'],
								children: DashboardInspect({ match: { params: { id: 15 } }, pageTitle: 'Statistics', module: 'Management' }),
							}),
						},
						{
							path: '/management/AnalyticsDashboard',
							exact: true,
							roles: ['Administrator', 'Manager'],
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="AnalyticsDashboard" moduleName="Analytics Dashboard" projects="Management">
										{AnalyticsDashboard()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/management/dataIntegrity',
							exact: true,
							roles: ['Administrator', 'Manager'],
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="DataIntegrity" moduleName="Data Integrity" projects="Management">
										{DataIntegrity()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/management/operators',
							exact: true,
							roles: ['Administrator', 'Manager'],
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="Operators" moduleName="Operators" projects="Management">
										{Operators()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/management/operators/details',
							exact: true,
							roles: ['Administrator', 'Manager'],
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="OperatorDetails" moduleName="Operator Details" projects="Management">
										{OperatorDetails()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/management/users',
							exact: true,
							roles: ['Administrator', 'Manager'],
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="Users" moduleName="Users" projects="Management">
										{Users()}
									</TrackableRoute>
								),
							}),
						},
						{
							path: '/management/settings',
							exact: true,
							component: () => AuthGuard({
								children: (
									<TrackableRoute moduleId="Settings" moduleName="Settings" projects="Management">
										{Settings()}
									</TrackableRoute>
								),
							}),
						},
						{ component: () => <Redirect to="/errors/error-404" /> },
					],
				},
			}),
		}),
	},
	{
		path: '/auth',
		component: AuthLayout,
		routes: [
			{ path: '/auth/login', exact: true, component: lazy(() => import('views/Auth/Login')) },
			{ path: '/auth/completeregistration', exact: true, component: lazy(() => import('views/Auth/CompleteRegistration')) },
			{ path: '/auth/resetpassword', exact: true, component: lazy(() => import('views/Auth/ResetPassword')) },
			{ path: '/auth/requestAccess', exact: true, component: lazy(() => import('views/Auth/RequestAccess')) },
			{ path: '/auth/requestAccessConfirmation', exact: true, component: lazy(() => import('views/Auth/RequestAccessConfirmation')) },
			{ path: '/auth/recover', exact: true, component: lazy(() => import('views/Auth/Recover')) },
			{ path: '/auth/recoverconfirmation', exact: true, component: lazy(() => import('views/Auth/RecoverConfirmation')) },
			{ component: () => <Redirect to="/errors/error-404" /> },
		],
	},
	{
		path: '/errors',
		component: ErrorLayout,
		routes: [
			{ path: '/errors/error-401', exact: true, component: lazy(() => import('views/Error401')) },
			{ path: '/errors/error-404', exact: true, component: lazy(() => import('views/Error404')) },
			{ path: '/errors/error-500', exact: true, component: lazy(() => import('views/Error500')) },
			{ component: () => <Redirect to="/errors/error-404" /> },
		],
	},
	{
		path: '*',
		component: () => AuthGuard({
			roles: ['Administrator'],
			children: DashboardLayout({
				route: {
					routes: [
						{ path: '/notifications', exact: true, component: lazy(() => import('views/Notifications')) },
						{ path: '/calendar', exact: true, component: lazy(() => import('views/Calendar')) },
						{ path: '/changelog', exact: true, component: lazy(() => import('views/Changelog')) },
						{ path: '/chat', exact: true, component: lazy(() => import('views/Chat')) },
						{ path: '/chat/:id', exact: true, component: lazy(() => import('views/Chat')) },
						{ path: '/dashboard/analytics', exact: true, component: DashboardAnalyticsView },
						{ path: '/dashboard/default', exact: true, component: DashboardDefaultView },
						{ path: '/invoices/:id', exact: true, component: lazy(() => import('views/InvoiceDetails')) },
						{ path: '/kanban-board', exact: true, component: lazy(() => import('views/KanbanBoard')) },
						{ path: '/mail', exact: true, component: lazy(() => import('views/Mail')) },
						{ path: '/manage/customers', exact: true, component: lazy(() => import('views/CustomerManagementList')) },
						{ path: '/manage/customers/:id', exact: true, component: lazy(() => import('views/CustomerManagementDetails')) },
						{ path: '/manage/customers/:id/:tab', exact: true, component: lazy(() => import('views/CustomerManagementDetails')) },
						{ path: '/manage/projects', exact: true, component: lazy(() => import('views/ProjectManagementList')) },
						{ path: '/manage/orders', exact: true, component: lazy(() => import('views/OrderManagementList')) },
						{ path: '/manage/orders/:id', exact: true, component: lazy(() => import('views/OrderManagementDetails')) },
						{ path: '/overview', exact: true, component: OverviewView },
						{ path: '/presentation', exact: true, component: PresentationView },
						{ path: '/profile/:id', exact: true, component: lazy(() => import('views/Profile')) },
						{ path: '/profile/:id/:tab', exact: true, component: lazy(() => import('views/Profile')) },
						{ path: '/projects/create', exact: true, component: lazy(() => import('views/ProjectCreate')) },
						{ path: '/projects/:id', exact: true, component: lazy(() => import('views/ProjectDetails')) },
						{ path: '/projects/:id/:tab', exact: true, component: lazy(() => import('views/ProjectDetails')) },
						{ path: '/projects', exact: true, component: lazy(() => import('views/ProjectList')) },
						{ path: '/settings', exact: true, component: lazy(() => import('views/Settings')) },
						{ path: '/settings/:tab', exact: true, component: lazy(() => import('views/Settings')) },
						{ path: '/social-feed', exact: true, component: lazy(() => import('views/SocialFeed')) },
						{ path: '/getting-started', exact: true, component: lazy(() => import('views/GettingStarted')) },
						{ component: () => <Redirect to="/errors/error-404" /> },
					],
				},
			}),
		}),
	},
];

export default routes;